import React from 'react'
import { t } from 'i18next'
import { Icon } from '@pandiafi/design-system'

import {
  DefectLocation,
  CreateDefectNotice,
  DefectNoticeDefectClasses,
  DefectNoticeEvent,
} from '../../../common/validations/defectNotice'
import { Contract } from '../../../common/validations'
import { FormValues } from './initialValues'
import { ResidentAppError } from '../../../common/error'

export const useDefectLocations = (contract: Contract) => {
  const target = [
    contract.addressDetails.staircaseLetter,
    contract.addressDetails.apartmentNumber,
  ]
    .filter(Boolean)
    .join(' ')

  return Object.values(DefectLocation).map((_, index) => ({
    id: String(index),
    label:
      index === 0
        ? t('defectNotice:defectLocations.contractTarget', {
            target,
          })
        : t('defectNotice:defectLocations.commonArea'),
  }))
}

type UseDefectClasses = {
  buttonItems: { id: string; icon: React.ReactNode; label: string }[]
  listItems: { id: string; label: string }[]
}

export const useDefectClasses = (
  defectClasses: DefectNoticeDefectClasses
): UseDefectClasses => {
  const icons = [
    <Icon icon="reparation" size="2x" />,
    <Icon icon="hvac" size="2x" />,
    <Icon icon="electricity" size="2x" />,
    <Icon icon="lock" size="2x" />,
  ]
  const classes = Object.values(defectClasses).map((defectClass, index) => {
    const isButtonItem = icons.length > index
    const label = t(`defectNotice:defectClasses.${defectClass}`)

    if (isButtonItem) {
      return {
        id: String(defectClass),
        icon: icons[index],
        label,
      }
    }
    return {
      id: String(defectClass),
      label,
    }
  })

  const filteredRemainingClasses = classes
    .slice(icons.length)
    .filter((item) => Number(item.id) !== 20 && Number(item.id) !== 21)
    .map((item) => ({
      id: String(item.id),
      label: item.label,
    }))

  return {
    buttonItems: classes.slice(
      0,
      icons.length
    ) as UseDefectClasses['buttonItems'],
    listItems: [
      ...filteredRemainingClasses,
      { id: '-1', label: t(`defectNotice:defectClasses.-1`) },
    ],
  }
}

export const formatFormValues = (
  formValues: FormValues,
  contractUuid: string
) => {
  const { title } = formValues
  const { description } = formValues
  const defectLocation = formValues.defectLocation
    ? formValues.defectLocation === '0'
      ? DefectLocation.CONTRACT_TARGET
      : DefectLocation.COMMON_AREA
    : ''
  const fileUuids = formValues.files.length
    ? formValues.files.map((file) => file.uuid)
    : []
  const defectClassId =
    formValues.defectClassId === ''
      ? ''
      : String(formValues.defectClassId) === '-1'
        ? null
        : Number(formValues.defectClassId)
  const noticeReporter =
    formValues.noticeReporter?.phoneNumber && formValues.noticeReporter?.email
      ? {
          phoneNumber: formValues.noticeReporter.phoneNumber,
          email: formValues.noticeReporter.email,
        }
      : null

  return {
    title,
    description,
    contractUuid,
    defectLocation,
    defectClassId,
    fileUuids,
    noticeReporter,
    hasPetsInApartment: formValues.hasPetsInApartment,
    masterKeyAccessAllowed: formValues.masterKeyAccessAllowed,
  } as CreateDefectNotice
}

export const createBlobUrl = async (signedUrl: string) => {
  // Download from S3
  const downloadResponse = await fetch(signedUrl)
  if (!downloadResponse.ok) {
    throw ResidentAppError.ClientError('FILE_FAILED_TO_LOAD', {
      message: 'Failed to load file from S3',
    })
  }
  const blob = await downloadResponse.blob()

  // Create a local URL and open it in a new tab
  return URL.createObjectURL(blob)
}

export const renderEventType = (evt: DefectNoticeEvent) => {
  switch (evt.eventType) {
    case 'created':
      return t('defectNotice:events.created')
    case 'work_order_linked':
      return t('defectNotice:events.workOrderLinked', {
        id: evt.workOrder?.id,
      })
    case 'completed':
      return t('defectNotice:events.completed')
    case 'work_note_created':
      return t('defectNotice:events.workNoteCreated', {
        note: evt.workNoteDescription,
      })
    case 'work_order_started': {
      return t('defectNotice:events.workOrderStarted', {
        id: evt.workOrder?.id,
      })
    }
    case 'work_order_accepted': {
      return t('defectNotice:events.workOrderAccepted', {
        id: evt.workOrder?.id,
      })
    }
    case 'work_order_completed': {
      return t('defectNotice:events.workOrderCompleted', {
        id: evt.workOrder?.id,
      })
    }
    default:
      return null
  }
}
